import { debounce } from '@mui/material';
import type { GridStackWidget } from 'gridstack';
import type { editor } from 'monaco-editor';
import { useEffect, useRef, type MutableRefObject } from 'react';
import type { ICardComponent } from '../../Components/CodeEditor/Devices/DeviceCardBuilder';
import { getMainProgramCode, getMainProgramModel } from '../../Components/CodeEditor/utils/mainProgramEditor';
import { useAppDispatch } from '../../redux/ReduxHooks';
import {
  buildDriveUnitPortOccupanciesFromHardwareCards,
  collectDriveUnitElectronicsFromHardwareCards,
} from '../../utils/buildDriveUnitPortOccupanciesFromHardwareCards';
import { isTrackedDriveUnitPort } from '../../utils/driveUnitPortSelection';
import { applyDriveUnitPortStreamSync, portChangeSyncsDashboardStreams, skipNextDriveUnitPortStreamSyncRef } from './useConfigurePanelPortChange';

/** Seeds port baselines from a pre-edit program snapshot so late hardware-card loads still detect Monaco port changes. */
function seedPrevPortsFromBaseline(prevPortsRef: MutableRefObject<Map<string, number>>, cards: ICardComponent[], baselineProgramCode: string): void {
  if (prevPortsRef.current.size > 0 || !cards.length || !baselineProgramCode.trim()) return;

  for (const row of buildDriveUnitPortOccupanciesFromHardwareCards(cards, undefined, baselineProgramCode)) {
    prevPortsRef.current.set(row.electronic, row.driveUnitPort);
  }
}

function resolveOriginalProgramCodeForStreamSync(baselineProgramCode: string, savedProjectCode: string, programText: string): string {
  if (baselineProgramCode.trim()) return baselineProgramCode;
  if (savedProjectCode.trim()) return savedProjectCode;
  return programText;
}

/**
 * When the user edits a drive-unit constructor port in Monaco, calls the same
 * `applyDriveUnitPortStreamSync` path as `useConfigurePanelPortChange` (Configure Hardware UI).
 */
export function useDriveUnitPortStreamSyncFromEditor(opts: {
  editor: editor.IStandaloneCodeEditor | null;
  hardwareCards: ICardComponent[];
  readOnly: boolean;
  projectId?: string;
  dashboardLayout: GridStackWidget[] | undefined;
  projectCode: string;
}): void {
  const dispatch = useAppDispatch();
  const prevPortsRef = useRef<Map<string, number>>(new Map());
  const baselineProgramCodeRef = useRef('');
  const syncPortsFromEditorRef = useRef<(() => void) | null>(null);
  const hardwareCardsRef = useRef(opts.hardwareCards);
  hardwareCardsRef.current = opts.hardwareCards;
  const projectCodeRef = useRef(opts.projectCode);
  projectCodeRef.current = opts.projectCode;
  const dashboardLayoutRef = useRef(opts.dashboardLayout);
  dashboardLayoutRef.current = opts.dashboardLayout;
  const readOnlyRef = useRef(opts.readOnly);
  readOnlyRef.current = opts.readOnly;
  const projectIdRef = useRef(opts.projectId);
  projectIdRef.current = opts.projectId;

  useEffect(() => {
    prevPortsRef.current = new Map();
    baselineProgramCodeRef.current = '';
  }, [opts.projectId]);

  useEffect(() => {
    const ed = opts.editor;
    if (!ed) return;

    const captureBaselineProgramCodeIfNeeded = () => {
      if (baselineProgramCodeRef.current.trim()) return;
      const captured = getMainProgramCode(ed, projectCodeRef.current);
      if (captured.trim()) baselineProgramCodeRef.current = captured;
    };

    captureBaselineProgramCodeIfNeeded();

    const trySyncPortsFromEditor = () => {
      captureBaselineProgramCodeIfNeeded();

      const skipStreamSync = skipNextDriveUnitPortStreamSyncRef.current;
      if (skipStreamSync) {
        skipNextDriveUnitPortStreamSyncRef.current = false;
      }

      const readOnly = readOnlyRef.current;
      const projectId = projectIdRef.current;
      if (readOnly || !projectId) return;

      const cards = hardwareCardsRef.current;
      if (!cards.length) return;

      const seedingPrevPorts = prevPortsRef.current.size === 0;
      seedPrevPortsFromBaseline(prevPortsRef, cards, baselineProgramCodeRef.current);

      const programText = getMainProgramCode(ed, projectCodeRef.current);
      const savedProjectCode = projectCodeRef.current;
      const originalProgramCode = resolveOriginalProgramCodeForStreamSync(baselineProgramCodeRef.current, savedProjectCode, programText);
      const occupancies = buildDriveUnitPortOccupanciesFromHardwareCards(cards, ed, programText);
      const knownElectronics = collectDriveUnitElectronicsFromHardwareCards(cards);

      for (const row of occupancies) {
        const parameterName = row.electronic.slice(row.electronic.lastIndexOf(':') + 1);
        const oldPort = prevPortsRef.current.get(row.electronic);
        const newPort = row.driveUnitPort;

        if (
          !skipStreamSync &&
          oldPort !== undefined &&
          oldPort !== newPort &&
          portChangeSyncsDashboardStreams(parameterName) &&
          isTrackedDriveUnitPort(oldPort) &&
          isTrackedDriveUnitPort(newPort)
        ) {
          applyDriveUnitPortStreamSync(dispatch, {
            readOnly,
            projectId,
            dashboardLayout: dashboardLayoutRef.current,
            desiredProgramCode: programText,
            baselineProgramCode: originalProgramCode,
            currentBufferCode: programText,
            electronic: row.electronic,
            parameterName,
            oldPort,
            newPort,
            driveUnitPorts: occupancies,
          });
        }

        prevPortsRef.current.set(row.electronic, newPort);
      }

      for (const electronic of [...prevPortsRef.current.keys()]) {
        if (!knownElectronics.has(electronic)) {
          prevPortsRef.current.delete(electronic);
        }
      }

      if (seedingPrevPorts) {
        baselineProgramCodeRef.current = programText;
      }
    };

    syncPortsFromEditorRef.current = trySyncPortsFromEditor;

    const debouncedSync = debounce(trySyncPortsFromEditor, 100);
    const onContentChange = () => debouncedSync();
    const disposable =
      typeof ed.onDidChangeModelContent === 'function'
        ? ed.onDidChangeModelContent(onContentChange)
        : getMainProgramModel(ed)?.onDidChangeContent(onContentChange);

    if (!disposable) return;

    return () => {
      syncPortsFromEditorRef.current = null;
      debouncedSync.clear();
      disposable.dispose();
    };
  }, [dispatch, opts.editor]);

  useEffect(() => {
    if (!opts.editor || !opts.hardwareCards.length) return;

    if (!baselineProgramCodeRef.current.trim()) {
      baselineProgramCodeRef.current = getMainProgramCode(opts.editor, projectCodeRef.current);
    }
    syncPortsFromEditorRef.current?.();
  }, [opts.projectId, opts.hardwareCards, opts.editor]);
}
